import React, { useState, useEffect, useCallback } from 'react';
import { useApp } from '@/context/AppContext';
import { TopBar } from '@/components/TopBar';
import { Page } from '@/components/Page';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import api, { errMsg } from '@/lib/api';
import { money } from '@/lib/helpers';
import BulkAddSheet from '@/components/BulkAddSheet';
import { Search, Plus, Minus, SlidersHorizontal, History, ArrowRight, Table2 } from 'lucide-react';

const REASONS = ['Physical stock count correction', 'Damaged item', 'Missing/lost item', 'Found stock', 'Opening stock correction', 'Other'];

export default function StockAdjustment() {
  const { stores, currentStore, can } = useApp();
  const showCost = !!(can && can('view_cost'));
  const [code, setCode] = useState('');
  const [store, setStore] = useState(currentStore || 'store-2');
  const [item, setItem] = useState(null);
  const [type, setType] = useState('ADD');
  const [qty, setQty] = useState(1);
  const [reason, setReason] = useState(REASONS[0]);
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [hist, setHist] = useState([]);
  const [total, setTotal] = useState(0);
  const [fCode, setFCode] = useState('');
  const [fStore, setFStore] = useState('');
  const [fReason, setFReason] = useState('');
  const [bulkOpen, setBulkOpen] = useState(false);
  const canAddStock = !!(can && can('stock_add'));

  const storeName = (id) => stores.find((s) => s.id === id)?.name || id;
  const newQty = item ? (type === 'ADD' ? item.current_qty + Number(qty || 0) : item.current_qty - Number(qty || 0)) : 0;
  const invalidReduce = type === 'REDUCE' && item && Number(qty) > item.current_qty;

  const lookup = async () => {
    if (!code.trim()) return;
    setItem(null);
    try {
      const r = await api.get('/stock/adjust/lookup', { params: { code: code.trim(), store_id: store } });
      setItem(r.data.data);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const loadHistory = useCallback(async () => {
    try {
      const params = {};
      if (fCode) params.code = fCode;
      if (fStore) params.store_id = fStore;
      if (fReason) params.reason = fReason;
      const r = await api.get('/stock/adjustments', { params });
      setHist(r.data.data.adjustments); setTotal(r.data.data.total);
    } catch (e) { console.error('Failed to load stock adjustments', e); }
  }, [fCode, fStore, fReason]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const apply = async () => {
    setBusy(true);
    try {
      const r = await api.post('/stock/adjustment', { code: item.code || code.trim(), store_id: store, type, quantity: Number(qty), reason, notes });
      const d = r.data.data;
      toast.success(`${type === 'ADD' ? 'Added' : 'Reduced'} ${qty} — ${d.old_qty} → ${d.new_qty}`);
      setItem((prev) => prev ? { ...prev, current_qty: d.new_qty } : prev);
      setNotes(''); setQty(1);
      loadHistory();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  return (
    <>
      <TopBar title="Stock Adjustment" showStore={false} back />
      <Page>
        <div className="mx-auto max-w-3xl space-y-4 px-4 py-4" data-testid="stock-adjustment-page">
          {/* Adjust card */}
          <div className="rounded-2xl border border-border bg-card p-4">
            <div className="mb-3 flex items-center gap-2"><SlidersHorizontal className="h-4 w-4 text-primary" /><p className="text-sm font-semibold">Adjust stock for a product</p>
              {canAddStock && (
                <Button variant="secondary" size="sm" className="ml-auto h-9 rounded-lg" onClick={() => setBulkOpen(true)} data-testid="adjust-bulk-add">
                  <Table2 className="mr-1.5 h-4 w-4" /> Bulk Add Items
                </Button>
              )}
            </div>
            {canAddStock && (
              <p className="-mt-1 mb-3 text-xs text-muted-foreground">Correcting a physical count with many <b>new/found</b> items? Use <b>Bulk Add Items</b> to paste or import a whole list at once.</p>
            )}
            <div className="grid gap-2 sm:grid-cols-[1fr_auto_auto]">
              <div>
                <Label className="text-xs text-muted-foreground">Product code / barcode</Label>
                <Input value={code} onChange={(e) => setCode(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && lookup()} placeholder="e.g. RM544A" className="mt-1 h-11 rounded-xl" data-testid="adjust-code-input" />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Store</Label>
                <Select value={store} onValueChange={(v) => { setStore(v); setItem(null); }}>
                  <SelectTrigger className="mt-1 h-11 w-36 rounded-xl" data-testid="adjust-store-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{stores.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="flex items-end"><Button onClick={lookup} className="h-11 rounded-xl" data-testid="adjust-search-btn"><Search className="mr-1 h-4 w-4" />Search</Button></div>
            </div>

            {item && (
              <div className="mt-4 rounded-xl border border-border p-3" data-testid="adjust-product-info">
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm sm:grid-cols-3">
                  <div><span className="text-xs text-muted-foreground">Product</span><p className="font-medium">{item.product}</p></div>
                  <div><span className="text-xs text-muted-foreground">Code / Barcode</span><p className="font-mono">{item.barcode}</p></div>
                  <div><span className="text-xs text-muted-foreground">Category</span><p>{item.category || '—'}</p></div>
                  <div><span className="text-xs text-muted-foreground">Size</span><p>{item.size || '—'}</p></div>
                  <div><span className="text-xs text-muted-foreground">Store</span><p>{storeName(item.store_id)}</p></div>
                  {showCost && <div><span className="text-xs text-muted-foreground">Stock value</span><p>{money(item.stock_value)}</p></div>}
                </div>

                <div className="mt-3 grid gap-3 sm:grid-cols-3">
                  <div>
                    <Label className="text-xs text-muted-foreground">Adjustment type</Label>
                    <div className="mt-1 flex gap-2">
                      <button onClick={() => setType('ADD')} className={`flex flex-1 items-center justify-center gap-1 rounded-xl border py-2.5 text-sm font-semibold ${type === 'ADD' ? 'border-success bg-success text-success-foreground' : 'border-border'}`} data-testid="adjust-type-add"><Plus className="h-4 w-4" />Add</button>
                      <button onClick={() => setType('REDUCE')} className={`flex flex-1 items-center justify-center gap-1 rounded-xl border py-2.5 text-sm font-semibold ${type === 'REDUCE' ? 'border-destructive bg-destructive text-destructive-foreground' : 'border-border'}`} data-testid="adjust-type-reduce"><Minus className="h-4 w-4" />Reduce</button>
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Quantity</Label>
                    <Input type="number" min={1} value={qty} onChange={(e) => setQty(e.target.value)} className="mt-1 h-11 rounded-xl" data-testid="adjust-qty-input" />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Reason</Label>
                    <Select value={reason} onValueChange={setReason}>
                      <SelectTrigger className="mt-1 h-11 rounded-xl" data-testid="adjust-reason-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{REASONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="mt-3">
                  <Label className="text-xs text-muted-foreground">Notes (optional)</Label>
                  <Input value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1 h-11 rounded-xl" data-testid="adjust-notes-input" />
                </div>

                {/* Preview */}
                <div className="mt-4 flex items-center justify-center gap-3 rounded-xl bg-secondary p-3 text-center" data-testid="adjust-preview">
                  <div><p className="text-xs text-muted-foreground">Current</p><p className="text-lg font-bold">{item.current_qty}</p></div>
                  <ArrowRight className="h-4 w-4 text-muted-foreground" />
                  <div><p className="text-xs text-muted-foreground">{type === 'ADD' ? 'Add' : 'Reduce'}</p><p className={`text-lg font-bold ${type === 'ADD' ? 'text-success' : 'text-destructive'}`}>{type === 'ADD' ? '+' : '−'}{qty || 0}</p></div>
                  <ArrowRight className="h-4 w-4 text-muted-foreground" />
                  <div><p className="text-xs text-muted-foreground">New stock</p><p className="text-lg font-bold" data-testid="adjust-new-qty">{invalidReduce ? '—' : newQty}</p></div>
                </div>
                {invalidReduce && <p className="mt-2 text-center text-xs text-destructive">Cannot reduce more than the {item.current_qty} available. Stock can never go negative.</p>}

                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button disabled={busy || !qty || Number(qty) < 1 || invalidReduce} className="mt-3 h-12 w-full rounded-xl" data-testid="adjust-apply-btn">Apply adjustment</Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Confirm stock adjustment</AlertDialogTitle>
                      <AlertDialogDescription>
                        {type} <b>{qty}</b> unit(s) of <b>{item.product}</b> ({item.barcode}) at <b>{storeName(store)}</b>.<br />
                        Stock: <b>{item.current_qty}</b> → <b>{newQty}</b>. Reason: {reason}.<br />
                        This creates a permanent adjustment record. It does not create any sale, purchase or return.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel data-testid="adjust-confirm-cancel">Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={apply} data-testid="adjust-confirm-apply">Confirm &amp; apply</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            )}
          </div>

          {/* History */}
          <div className="rounded-2xl border border-border bg-card p-4">
            <div className="mb-3 flex items-center gap-2"><History className="h-4 w-4 text-primary" /><p className="text-sm font-semibold">Stock Adjustment History</p>
              <span className="ml-auto rounded-full bg-secondary px-2 py-0.5 text-xs font-semibold" data-testid="adjust-total">Total: {total}</span></div>
            <div className="mb-3 grid gap-2 sm:grid-cols-3">
              <Input value={fCode} onChange={(e) => setFCode(e.target.value)} placeholder="Filter by code" className="h-10 rounded-lg" data-testid="hist-filter-code" />
              <Select value={fStore || 'all'} onValueChange={(v) => setFStore(v === 'all' ? '' : v)}>
                <SelectTrigger className="h-10 rounded-lg" data-testid="hist-filter-store"><SelectValue placeholder="All stores" /></SelectTrigger>
                <SelectContent><SelectItem value="all">All stores</SelectItem>{stores.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={fReason || 'all'} onValueChange={(v) => setFReason(v === 'all' ? '' : v)}>
                <SelectTrigger className="h-10 rounded-lg" data-testid="hist-filter-reason"><SelectValue placeholder="All reasons" /></SelectTrigger>
                <SelectContent><SelectItem value="all">All reasons</SelectItem>{REASONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {hist.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground" data-testid="hist-empty">No stock adjustments yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm" data-testid="hist-table">
                  <thead><tr className="border-b border-border text-xs text-muted-foreground">
                    <th className="py-2 pr-3">When</th><th className="pr-3">Code</th><th className="pr-3">Store</th><th className="pr-3">Type</th><th className="pr-3">Qty</th><th className="pr-3">Old→New</th><th className="pr-3">Reason</th><th>By</th>
                  </tr></thead>
                  <tbody>
                    {hist.map((a) => (
                      <tr key={a.id} className="border-b border-border/50">
                        <td className="py-2 pr-3 whitespace-nowrap text-xs">{new Date(a.ts).toLocaleString()}</td>
                        <td className="pr-3 font-mono text-xs">{a.code}</td>
                        <td className="pr-3 text-xs">{a.store_name || storeName(a.store_id)}</td>
                        <td className="pr-3"><span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${a.type === 'ADD' ? 'bg-success text-success-foreground' : 'bg-destructive text-destructive-foreground'}`}>{a.type}</span></td>
                        <td className="pr-3">{a.quantity}</td>
                        <td className="pr-3 tabular-nums">{a.old_qty} → {a.new_qty}</td>
                        <td className="pr-3 text-xs">{a.reason}</td>
                        <td className="text-xs">{a.user}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
        <BulkAddSheet open={bulkOpen} onOpenChange={setBulkOpen} onDone={loadHistory} />
      </Page>
    </>
  );
}
